import styled, { css } from 'styled-components';
import { Typography } from '../../global/theme';
import { mobile_xl } from '../../global/mediaQueryMixin';
import { AlignCenter, FlexBetween, HideScrollbar, LineClamp } from '../../global/commonStyle';

interface Props {
  isMiddleContainer?: boolean;
  isNewRow?: boolean;
  isDelete?: boolean;
  applyMargin?: boolean;
}

const Container = styled.div`
  padding-inline: var(--space-24);
  height: 100dvh;
  position: relative;
  ${HideScrollbar}
  ${mobile_xl(css`
    padding-inline: var(--space-20);
  `)}
`;

const Group = styled.div<Props>`
  width: 100%;
  max-width: 662px;
  margin: var(--space-0) auto var(--space-24);
  ${mobile_xl(css`
    margin-bottom: var(--space-96);
  `)}
`;
const Content = styled.div`
  background-color: var(--background-secondary);
`;

const Icon = styled.div`
  position: relative;
  display: flex;
  cursor: pointer;

  .more-icon {
    fill: var(--text-secondary);
    stroke: var(--text-secondary);
    &:hover {
      background: var(--neutral-secondary);
      border-radius: var(--border-radius-4);
    }
  }
  .profile-image {
    border-radius: var(--border-radius-full);
    width: 58px;
    height: 58px;
    object-fit: cover;
  }
  .square-profile-image {
    border-radius: var(--border-radius-8);
    width: 46px;
    height: 46px;
    object-fit: cover;
  }
  .empty-profile-image {
    border-radius: var(--border-radius-full);
    width: 60px;
    height: 60px;
    object-fit: cover;
  }
  &.profile-image-container {
    border: 0.5px solid var(--border-primary);
    border-radius: var(--border-radius-full);
  }
  &.square-profile-image-container {
    border: 0.5px solid var(--border-primary);
    border-radius: var(--border-radius-8);
  }
  &:hover {
    .edit-profile {
      display: flex;
    }
    .profile-image {
      filter: blur(1.5px);
      -webkit-filter: blur(1.5px);
    }
    .square-profile-image {
      filter: blur(1.5px);
      -webkit-filter: blur(1.5px);
    }
    .workspace-icon-wrapper {
      filter: blur(1.5px);
      -webkit-filter: blur(1.5px);
    }
    .empty-edit-profile {
      display: flex;
    }
  }
`;

const Details = styled.div<Props>`
  display: flex;
  flex-direction: column;
  gap: var(--space-20);
  padding: var(--space-20);
  background-color: var(--background-primary);
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-8);
  align-items: flex-start;
  ${(props) =>
    props.isDelete &&
    css`
      gap: var(--space-12);
    `}
  ${mobile_xl(css`
    margin-top: var(--space-8);
  `)}
`;

const Title1 = styled.h2`
  ${Typography.heading_md}
  color: var(--text-primary);
  margin: var(--space-0);
`;

const ImageUploadText = styled.p<Props>`
  ${Typography.body_md_medium}
  color: var(--text-primary);
  margin: var(--space-0);
  cursor: pointer;
  &:hover {
    color: var(--text-secondary);
  }
`;

const Text = styled.p`
  ${Typography.body_md}
  color: var(--text-secondary);
  margin: var(--space-0);
  span {
    font-weight: 700;
  }
`;

const UserProfile = styled.div`
  ${AlignCenter}
  gap: var(--space-16);
`;

const UploadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  ${mobile_xl(css`
    gap: var(--space-2);
  `)}
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-20);
  ${mobile_xl(css`
    gap: var(--space-16);
  `)}
`;

const FormBlock = styled.div<Props>`
  display: flex;
  gap: var(--space-16);
  flex-wrap: wrap;
  ${(props: Props) =>
    props.isNewRow &&
    css`
      flex-direction: column;
    `}
  ${mobile_xl(css`
    width: 100%;
  `)}
`;

const Form = styled.div<Props>`
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  ${mobile_xl(css`
    width: 100%;
  `)}
`;

const Label = styled.label`
  ${Typography.body_md_medium}
  color: var(--text-secondary);
`;

const ProjectContent = styled.div<Props>`
  max-width: 662px;
  background-color: var(--background-primary);
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-8);
  ${mobile_xl(css`
    margin-top: var(--space-8);
  `)}
`;

const Text1 = styled.p`
  ${Typography.body_md_medium}
  color: var(--brand-primary);
  margin: var(--space-0);
  cursor: pointer;
  display: inline-flex;
`;

// payment
const PaymentBlock = styled.div`
  display: flex;
  align-items: stretch;
  flex-direction: column;
  gap: var(--space-20);
  padding: var(--space-20);
  background-color: var(--background-primary);
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-8);
`;

const TextBlocks = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
`;

const Text3 = styled.a`
  ${Typography.body_md_medium}
  color: var(--brand-primary);
  margin: var(--space-0);
  text-decoration: none;
  width: fit-content;
`;

const Teammate = styled.div`
  padding: var(--space-12) var(--space-16);
  ${FlexBetween}
  border-bottom: 0.5px solid var(--border-primary);
  :last-child {
    border-bottom: none;
  }
  .pending {
    opacity: 0.6;
  }
  ${mobile_xl(css`
    gap: var(--space-12);
  `)}
`;
const PersonInfo = styled.div`
  ${AlignCenter}
  gap: var(--space-12);
`;

const TeammateName = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  h1 {
    ${Typography.body_lg_medium}
    color: var(--text-primary);
    margin: var(--space-0);
    ${LineClamp(1)}
    word-break: break-all;
  }
  p {
    ${Typography.body_md}
    color: var(--text-secondary);
    margin: var(--space-0);
    ${LineClamp(1)}
    word-break: break-all;
  }
`;

const PendingButton = styled.button`
  padding: var(--space-2) var(--space-5);
  ${Typography.body_sm}
  color: var(--text-secondary);
  background: var(--border-primary);
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-6);
  ${mobile_xl(css`
    padding-block: var(--space-0);
  `)}
`;
const PersonalText = styled.div`
  padding-block: var(--space-1);
  ${Typography.heading_lg}
  color: var(--text-primary);
`;
const Header = styled.div<Props>`
  max-width: 662px;
  width: 100%;
  ${FlexBetween}
  padding-block: var(--space-14);
  gap: var(--space-24);
  margin: ${({ applyMargin }) => (applyMargin ? 'var(--space-0) auto' : 'unset')};
`;
const SubmitBtn = styled.div`
  display: flex;
  gap: var(--space-10);
`;

const NotificationContent = styled.div`
  background-color: var(--background-primary);
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-8);
  width: 100%;
  ${mobile_xl(css`
    border: unset;
  `)}
`;
const NotificationDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-12);
  ${mobile_xl(css`
    gap: var(--space-4);
  `)}
`;
const Pending = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--space-12);
  align-items: center;
  .active {
    background: var(--neutral-secondary);
    border-radius: var(--border-radius-4);
  }
`;

const AdminText = styled.div`
  padding: var(--space-2) var(--space-5);
  ${Typography.body_sm}
  color: var(--text-secondary);
  background: var(--border-primary);
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-6);
  ${mobile_xl(css`
    padding-block: var(--space-0);
  `)}
`;

const RightDiv = styled.div`
  ${AlignCenter}
  gap: var(--space-12);
  .active {
    display: flex;
    background: var(--neutral-secondary);
    border-radius: var(--border-radius-4);
  }
`;

const ColumnContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
`;
const Row = styled.div`
  ${FlexBetween}
`;
const ConnectBtn = styled.div`
  padding: var(--space-3) var(--space-8);
  border-radius: var(--border-radius-6);
  border: 1px solid var(--neutral-secondary);
  background: var(--neutral-secondary);
  color: var(--brand-primary);
  ${Typography.body_md_medium}
`;

const ProjectHeadingTop = styled.div`
  padding: var(--space-12) var(--space-14);
  ${AlignCenter}
  gap: var(--space-10);
  ${mobile_xl(css`
    padding-inline: var(--space-0);
  `)}
`;

const SelectedText = styled.div`
  position: absolute;
  top: 8px;
  left: 12px;
  pointer-events: none;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 218px;
  width: 100%;
  white-space: nowrap;
  ${Typography.body_lg}
`;
const Overlay = styled.div`
  background-color: var(--background-hover);
  top: 0;
  left: 0;
  width: 58px;
  height: 58px;
  position: absolute;
  display: none;
  justify-content: center;
  border-radius: var(--border-radius-full);
  cursor: pointer;
  &.empty-edit-profile {
    width: 60px;
    height: 60px;
  }
`;
const EditIcon = styled.div`
  display: flex;
  justify-content: center;
  margin: auto;
`;

const NotificationForm = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  ${mobile_xl(css`
    width: calc(100% - 40px);
    padding: var(--space-8) var(--space-20) var(--space-20);
    background-color: var(--background-primary);
    border: 0.5px solid var(--border-primary);
    border-radius: var(--border-radius-8);
    gap: var(--space-12);
    margin-top: var(--space-8);
  `)}
`;

// general/organisation profile screen
const OrganizationForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-20);
  ${mobile_xl(css`
    margin-block: var(--space-8) var(--space-80);
    gap: var(--space-24);
  `)}
`;
const OrganizationFormDetails = styled.div<Props>`
  display: flex;
  flex-direction: column;
  gap: var(--space-20);
  padding: var(--space-20);
  background-color: var(--background-primary);
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-8);
  align-items: flex-start;
  ${(props) =>
    props.isDelete &&
    css`
      gap: var(--space-12);
    `}
`;
const OrganizationDeleteBlock = styled.div`
  display: flex;
  gap: var(--space-8);
  flex-direction: column;
  ${mobile_xl(css`
    gap: var(--space-4);
    width: 100%;
  `)}
`;

const WorkspaceIcon = styled.div`
  background-color: var(--background-tertiary);
  width: 46px;
  height: 46px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--border-radius-8);
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-8);
  overflow: hidden;
  .workspace-icon {
    path {
      fill: var(--text-secondary);
    }
    rect {
      fill: var(--background-tertiary);
    }
  }
`;

const OverlaySquare = styled.div`
  background-color: var(--background-hover);
  top: 0;
  left: 0;
  width: 46px;
  height: 46px;
  position: absolute;
  display: none;
  justify-content: center;
  border-radius: var(--border-radius-8);
  cursor: pointer;
  &.empty-edit-profile {
    top: 0.5px;
    left: 0.5px;
  }
`;
export {
  Container,
  Group,
  Content,
  Icon,
  Details,
  Title1,
  Text,
  UserProfile,
  UploadText,
  ImageUploadText,
  FormBlock,
  Form,
  Label,
  ProjectContent,
  Text1,
  PaymentBlock,
  Text3,
  Teammate,
  PersonInfo,
  TeammateName,
  PendingButton,
  SubmitBtn,
  PersonalText,
  Header,
  NotificationDiv,
  NotificationContent,
  Pending,
  AdminText,
  RightDiv,
  ColumnContent,
  TextBlocks,
  Row,
  ConnectBtn,
  ProjectHeadingTop,
  SelectedText,
  Overlay,
  EditIcon,
  FormSection,
  NotificationForm,
  OrganizationForm,
  OrganizationFormDetails,
  OrganizationDeleteBlock,
  WorkspaceIcon,
  OverlaySquare
};
